/* ObiHub OS — temel primitive'ler (OS-1A).  Semantik HTML, odak görünürlüğü, disabled/loading ve ARIA davranışları.
   Üretim sayfalarına BAĞLI DEĞİLDİR; mevcut uygulama koduna, yardımcı stil çatılarına ve yönlendirici kütüphanesine bağımlılığı YOKTUR.
   Geçersiz prop (bilinmeyen varyant/ton/etiket, eksik erişilebilir ad) sessizce düzeltilmez: hata fırlatır. */
import React from "react";
import { tokens } from "./tokens";
import "./obihub-os.css";

const cx = (...parts) => parts.filter(Boolean).join(" ");
const SPACE_KEYS = Object.keys(tokens.space);
const oneOf = (name, value, allowed) => {
  if (!allowed.includes(value)) throw new Error(`${name}: geçersiz değer "${String(value)}" (izinli: ${allowed.join(", ")})`);
  return value;
};
const hasContent = (children) => React.Children.toArray(children).some((c) => (typeof c === "string" ? c.trim() !== "" : c !== null && c !== undefined && c !== false));

const Spinner = () => (
  <svg className="os-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const BUTTON_VARIANTS = ["primary", "secondary", "tertiary", "danger"];
const BUTTON_SIZES = ["sm", "md", "lg"];

export const OsButton = React.forwardRef(function OsButton(
  { variant = "secondary", size = "md", loading = false, disabled = false, type = "button", iconStart = null, className, onClick, children, ...rest }, ref) {
  oneOf("OsButton.variant", variant, BUTTON_VARIANTS);
  oneOf("OsButton.size", size, BUTTON_SIZES);
  oneOf("OsButton.type", type, ["button", "submit", "reset"]);
  const handleClick = (event) => {
    if (loading) {                                   // yükleniyor: odaklanabilir kalır ama eylem tetiklenmez
      event.preventDefault();
      return;
    }
    if (onClick) onClick(event);
  };
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled}
      aria-busy={loading ? "true" : undefined}
      aria-disabled={loading ? "true" : undefined}
      className={cx("os-btn", `os-btn--${variant}`, `os-btn--${size}`, className)}
      onClick={handleClick}
    >
      {loading ? <Spinner /> : iconStart}
      {children}
      {loading ? <span className="os-visually-hidden">Yükleniyor</span> : null}
    </button>
  );
});

export const OsIconButton = React.forwardRef(function OsIconButton({ label, size = "md", disabled = false, type = "button", className, children, ...rest }, ref) {
  if (typeof label !== "string" || label.trim() === "") throw new Error("OsIconButton: erişilebilir ad (label) zorunludur");
  oneOf("OsIconButton.size", size, BUTTON_SIZES);
  return (
    <button {...rest} ref={ref} type={type} disabled={disabled} aria-label={label} title={label} className={cx("os-iconbtn", `os-iconbtn--${size}`, className)}>
      {children}
    </button>
  );
});

const TONES = ["neutral", "info", "success", "warning", "danger", "review", "blocked"];
// Her ton AYRI biçimli bir ikon taşır: anlam yalnız renkle iletilmez.
const TONE_ICON = {
  neutral: <circle cx="12" cy="12" r="4" fill="currentColor" />,
  info: <><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" /><path d="M12 11v6M12 7.5v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></>,
  success: <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />,
  warning: <path d="M12 4l9 16H3L12 4zM12 10v4M12 17v.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />,
  danger: <path d="M7 3h10l4 4v10l-4 4H7l-4-4V7l4-4zM9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />,
  review: <><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" /><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" stroke="currentColor" strokeWidth="2" /></>,
  blocked: <><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" /><path d="M5.6 5.6l12.8 12.8" stroke="currentColor" strokeWidth="2" /></>,
};

export function OsBadge({ tone = "neutral", className, children, ...rest }) {
  oneOf("OsBadge.tone", tone, TONES);
  if (!hasContent(children)) throw new Error("OsBadge: metin zorunludur (anlam yalnız renk/ikonla verilemez)");
  return (
    <span {...rest} className={cx("os-badge", className)} data-tone={tone}>
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">{TONE_ICON[tone]}</svg>
      <span>{children}</span>
    </span>
  );
}

const SURFACE_TAGS = ["div", "section", "article", "aside", "header", "footer", "nav", "main"];
const ELEVATIONS = ["flat", "raised", "overlay"];

export const OsSurface = React.forwardRef(function OsSurface({ as: Tag = "div", elevation = "flat", padding, className, children, ...rest }, ref) {
  oneOf("OsSurface.as", Tag, SURFACE_TAGS);
  oneOf("OsSurface.elevation", elevation, ELEVATIONS);
  if (padding !== undefined) oneOf("OsSurface.padding", String(padding), SPACE_KEYS);
  return <Tag {...rest} ref={ref} className={cx("os-surface", `os-surface--${elevation}`, padding !== undefined && `os-pad-${padding}`, className)}>{children}</Tag>;
});

const FLOW_TAGS = ["div", "section", "ul", "ol", "span", "nav"];
const ALIGN = ["start", "center", "end", "stretch"];
const JUSTIFY = ["start", "center", "end", "between"];

function flowClasses(base, { gap, align, justify, wrap }) {
  if (gap !== undefined) oneOf(`${base}.gap`, String(gap), SPACE_KEYS);
  if (align !== undefined) oneOf(`${base}.align`, align, ALIGN);
  if (justify !== undefined) oneOf(`${base}.justify`, justify, JUSTIFY);
  return cx(base === "OsStack" ? "os-stack" : "os-inline", gap !== undefined && `os-gap-${gap}`, align && `os-align-${align}`, justify && `os-justify-${justify}`, wrap && "os-wrap");
}

export const OsStack = React.forwardRef(function OsStack({ as: Tag = "div", gap, align, justify, className, children, ...rest }, ref) {
  oneOf("OsStack.as", Tag, FLOW_TAGS);
  return <Tag {...rest} ref={ref} className={cx(flowClasses("OsStack", { gap, align, justify }), className)}>{children}</Tag>;
});

export const OsInline = React.forwardRef(function OsInline({ as: Tag = "div", gap, align, justify, wrap = false, className, children, ...rest }, ref) {
  oneOf("OsInline.as", Tag, FLOW_TAGS);
  return <Tag {...rest} ref={ref} className={cx(flowClasses("OsInline", { gap, align, justify, wrap }), className)}>{children}</Tag>;
});

const TEXT_TAGS = ["span", "p", "h1", "h2", "h3", "h4", "h5", "h6", "label", "div", "strong", "em", "code", "small"];
const TEXT_VARIANTS = ["display", "title", "heading", "body", "caption", "label", "mono"];
const TEXT_TONES = ["default", "muted", "danger", "success", "warning"];

export const OsText = React.forwardRef(function OsText({ as: Tag = "span", variant = "body", tone = "default", numeric = false, className, children, ...rest }, ref) {
  oneOf("OsText.as", Tag, TEXT_TAGS);
  oneOf("OsText.variant", variant, TEXT_VARIANTS);
  oneOf("OsText.tone", tone, TEXT_TONES);
  return <Tag {...rest} ref={ref} className={cx("os-text", `os-text--${variant}`, `os-tone-${tone}`, numeric && "os-numeric", className)}>{children}</Tag>;
});

export function OsSkeleton({ shape = "line", label, className, style, ...rest }) {
  oneOf("OsSkeleton.shape", shape, ["line", "block", "circle"]);
  const classes = cx("os-skeleton", `os-skeleton--${shape}`, className);
  if (label) {
    return <span {...rest} className={classes} style={style} role="status"><span className="os-visually-hidden">{label}</span></span>;
  }
  return <span {...rest} className={classes} style={style} aria-hidden="true" />;
}

const EMPTY_ROLE = { empty: null, error: "alert", blocked: "status", review: "status" };

export function OsEmptyState({ kind = "empty", title, description, icon = null, action = null, className, ...rest }) {
  oneOf("OsEmptyState.kind", kind, Object.keys(EMPTY_ROLE));
  if (typeof title !== "string" || title.trim() === "") throw new Error("OsEmptyState: başlık zorunludur");
  const role = EMPTY_ROLE[kind] || undefined;
  return (
    <div {...rest} className={cx("os-empty", className)} data-kind={kind} role={role}>
      {icon ? <span className="os-empty__icon" aria-hidden="true">{icon}</span> : null}
      <OsText as="h3" variant="heading">{title}</OsText>
      {description ? <OsText as="p" variant="body" tone="muted">{description}</OsText> : null}
      {action}
    </div>
  );
}
